import React, { useState, useEffect } from 'react';
import { Plus, Search, MapPin, Bed, Bath, Home as HomeIcon, Edit2, Trash2, Upload, X, ExternalLink, Loader } from 'lucide-react';
import api from '../lib/api';
import Modal from '../components/Modal';
import { Input, Select, Textarea } from '../components/Form';
import LocationPicker from '../components/LocationPicker';

interface Address {
  id?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface PropertyPhoto {
  id?: string;
  url: string;
  file?: File;
  order: number;
}

interface PropertyOwner {
  personId: string;
  percentage?: number | string;
  startDate?: string;
  person?: {
    id: string;
    name: string;
  };
}

interface PersonOption {
  id: string;
  name: string;
  document?: string;
}

interface Property {
  id: string;
  code: string;
  type: string;
  status: string;
  title: string;
  description?: string;
  bedrooms?: number;
  bathrooms?: number;
  parkingSpaces?: number;
  totalArea?: number;
  builtArea?: number;
  usableArea?: number;
  salePrice?: number | string;
  rentPrice?: number | string;
  seguroFianca?: number | string;
  seguroIncendio?: number | string;
  iptu?: number | string;
  condoFee?: number | string;
  address?: Address;
  latitude?: number | string;
  longitude?: number | string;
  isActive: boolean;
  media?: PropertyPhoto[];
  owners?: PropertyOwner[];
}

const formatCurrencyBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

const parseCurrencyBRL = (value: string): number => {
  if (!value) return 0;
  const cleaned = value
    .replace(/\s/g, '')
    .replace(/[R$]/g, '')
    .replace(/\./g, '')
    .replace(/,/g, '.')
    .replace(/[^0-9.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

export default function PropertiesPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [people, setPeople] = useState<PersonOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [photos, setPhotos] = useState<PropertyPhoto[]>([]);
  const [loadingCep, setLoadingCep] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const handleImportSite = async () => {
    if (importing) return;
    if (!confirm('Importar imóveis do site (com fotos) para o sistema? Pode levar alguns minutos.')) return;
    setImporting(true);
    setImportMsg('Importando... isso pode levar alguns minutos.');
    try {
      const { data } = await api.post('/site-import/run', { limit: 5 });
      setImportMsg(
        `Importados ${data.criados} · já existiam ${data.jaExistiam} · erros ${data.erros}. ` +
          (data.restantes > 0 ? `Faltam ~${data.restantes} — clique novamente para continuar.` : 'Tudo importado!'),
      );
      await loadProperties();
    } catch (e: any) {
      setImportMsg(e?.response?.data?.message || 'Falha na importação.');
    } finally {
      setImporting(false);
    }
  };

  const [formData, setFormData] = useState({
    code: '',
    type: 'HOUSE',
    status: 'AVAILABLE',
    title: '',
    description: '',
    bedrooms: 0,
    bathrooms: 0,
    parkingSpaces: 0,
    totalArea: 0,
    builtArea: 0,
    usableArea: 0,
    salePrice: 0,
    rentPrice: 0,
    seguroFianca: 0,
    seguroIncendio: 0,
    iptuValue: 0,
    condoFee: 0,
    address: '',
    addressNumber: '',
    addressComplement: '',
    neighborhood: '',
    city: 'Curitiba',
    state: 'PR',
    postalCode: '',
    latitude: 0,
    longitude: 0,
    ownerPersonId: '',
    ownerPercentage: 100,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadProperties(), loadPeople()]);
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const response = await api.get('/properties?limit=1000');
      setProperties(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
    }
  };

  const loadPeople = async () => {
    try {
      const response = await api.get('/people?limit=500');
      setPeople(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar pessoas para proprietário:', error);
      setPeople([]);
    }
  };

  const handleCepBlur = async () => {
    const cep = formData.postalCode.replace(/\D/g, '');
    
    if (cep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (!data.erro) {
        setFormData({
          ...formData,
          address: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
        });

        // Buscar coordenadas no Google Maps Geocoding API
        await fetchCoordinates(data.logradouro, data.localidade, data.uf);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setLoadingCep(false);
    }
  };

  const fetchCoordinates = async (street: string, city: string, state: string) => {
    try {
      const address = `${street}, ${city}, ${state}, Brasil`;
      const encodedAddress = encodeURIComponent(address);
      
      // Usando Nominatim (OpenStreetMap) - gratuito e sem necessidade de API key
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        setFormData((prev) => ({
          ...prev,
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar coordenadas:', error);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (photos.length + files.length > 20) {
      alert('Máximo de 20 fotos permitidas');
      return;
    }

    const newPhotos: PropertyPhoto[] = files.map((file, index) => ({
      url: URL.createObjectURL(file),
      file,
      order: photos.length + index,
    }));

    setPhotos([...photos, ...newPhotos]);
  };

  const removePhoto = async (index: number) => {
    const photo = photos[index];
    // Foto já salva (tem id) e estamos editando: apaga no backend de fato.
    if (photo?.id && selectedProperty?.id) {
      try {
        await api.delete(`/properties/${selectedProperty.id}/media/${photo.id}`);
      } catch (e) {
        console.error('Falha ao apagar foto:', e);
        alert('Não foi possível apagar a foto. Tente novamente.');
        return;
      }
    }
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const getGoogleMapsUrl = () => {
    const lat = toNumber(formData.latitude);
    const lng = toNumber(formData.longitude);

    if (lat === undefined || lng === undefined) {
      const address = `${formData.address}, ${formData.addressNumber}, ${formData.neighborhood}, ${formData.city}, ${formData.state}`;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    // ?q=lat,lng com zoom: crava o pino exatamente no ponto (mais confiável
    // que search/?api=1&query=, que às vezes abre como busca aproximada).
    return `https://www.google.com/maps?q=${lat},${lng}&z=18`;
  };

  const handleOpenModal = (property?: Property) => {
    const firstOwner = property?.owners?.[0];
    if (property) {
      setSelectedProperty(property);
      setFormData({
        code: property.code || '',
        type: property.type || 'HOUSE',
        status: property.status || 'AVAILABLE',
        title: property.title || '',
        description: property.description || '',
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 0,
        parkingSpaces: property.parkingSpaces || 0,
        totalArea: property.totalArea || 0,
        builtArea: property.builtArea || 0,
        usableArea: property.usableArea || 0,
        salePrice: toNumber(property.salePrice) || 0,
        rentPrice: toNumber(property.rentPrice) || 0,
        seguroFianca: toNumber(property.seguroFianca) || 0,
        seguroIncendio: toNumber(property.seguroIncendio) || 0,
        iptuValue: toNumber(property.iptu) || 0,
        condoFee: toNumber(property.condoFee) || 0,
        address: property.address?.street || '',
        addressNumber: property.address?.number || '',
        addressComplement: property.address?.complement || '',
        neighborhood: property.address?.neighborhood || '',
        city: property.address?.city || 'Curitiba',
        state: property.address?.state || 'PR',
        postalCode: property.address?.zipCode || '',
        latitude: toNumber(property.latitude) || 0,
        longitude: toNumber(property.longitude) || 0,
        ownerPersonId: firstOwner?.personId || '',
        ownerPercentage: toNumber(firstOwner?.percentage) || 100,
        isActive: property.isActive !== undefined ? property.isActive : true,
      });
      setPhotos(property.media || []);
    } else {
      setSelectedProperty(null);
      setFormData({
        code: '',
        type: 'HOUSE',
        status: 'AVAILABLE',
        title: '',
        description: '',
        bedrooms: 0,
        bathrooms: 0,
        parkingSpaces: 0,
        totalArea: 0,
        builtArea: 0,
        usableArea: 0,
        salePrice: 0,
        rentPrice: 0,
        seguroFianca: 0,
        seguroIncendio: 0,
        iptuValue: 0,
        condoFee: 0,
        address: '',
        addressNumber: '',
        addressComplement: '',
        neighborhood: '',
        city: 'Curitiba',
        state: 'PR',
        postalCode: '',
        latitude: 0,
        longitude: 0,
        ownerPersonId: '',
        ownerPercentage: 100,
        isActive: true,
      });
      setPhotos([]);
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProperty(null);
    setPhotos([]);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Código é obrigatório';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Endereço é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        code: formData.code,
        type: formData.type,
        status: formData.status,
        title: formData.title,
        description: formData.description || undefined,
        bedrooms: Number(formData.bedrooms) || undefined,
        bathrooms: Number(formData.bathrooms) || undefined,
        parkingSpaces: Number(formData.parkingSpaces) || undefined,
        totalArea: Number(formData.totalArea) || undefined,
        builtArea: Number(formData.builtArea) || undefined,
        usableArea: Number(formData.usableArea) || undefined,
        salePrice: Number(formData.salePrice) || undefined,
        rentPrice: Number(formData.rentPrice) || undefined,
        seguroFianca: Number(formData.seguroFianca) || undefined,
        seguroIncendio: Number(formData.seguroIncendio) || undefined,
        iptu: Number(formData.iptuValue) || undefined,
        condoFee: Number(formData.condoFee) || undefined,
        latitude: toNumber(formData.latitude) || undefined,
        longitude: toNumber(formData.longitude) || undefined,
        address: {
          street: formData.address,
          number: formData.addressNumber,
          complement: formData.addressComplement || undefined,
          neighborhood: formData.neighborhood,
          city: formData.city,
          state: formData.state,
          zipCode: formData.postalCode,
        },
        owners: formData.ownerPersonId
          ? [
              {
                personId: formData.ownerPersonId,
                percentage: Number(formData.ownerPercentage) || 100,
                startDate: new Date().toISOString().slice(0, 10),
              },
            ]
          : undefined,
      };

      console.log('Payload sendo enviado:', JSON.stringify(payload, null, 2));

      let propertyId: string | undefined;
      if (selectedProperty) {
        await api.patch(`/properties/${selectedProperty.id}`, payload);
        propertyId = selectedProperty.id;
      } else {
        const { data: created } = await api.post('/properties', payload);
        propertyId = created?.id;
      }

      // Upload das fotos novas (as que têm arquivo) para o backend.
      // Usa fetch nativo: o navegador monta o multipart/form-data com o
      // boundary correto (axios + Content-Type padrão do cliente quebrava isso).
      const newPhotos = photos.filter((p) => p.file);
      if (propertyId && newPhotos.length > 0) {
        const formDataPhotos = new FormData();
        newPhotos.forEach((photo) => {
          if (photo.file) formDataPhotos.append('files', photo.file);
        });
        try {
          const token = localStorage.getItem('access_token');
          const baseURL = api.defaults.baseURL || '/api';
          const resp = await fetch(`${baseURL}/properties/${propertyId}/media`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            body: formDataPhotos,
          });
          if (!resp.ok) {
            const t = await resp.text().catch(() => '');
            throw new Error(`HTTP ${resp.status} ${t}`);
          }
        } catch (e) {
          console.error('Falha ao enviar fotos:', e);
          alert('Imóvel salvo, mas houve falha ao enviar as fotos. Tente reenviá-las editando o imóvel.');
        }
      }

      await loadProperties();
      handleCloseModal();
    } catch (error: any) {
      console.error('Erro ao salvar imóvel:', error);
      console.error('Resposta do servidor:', error.response?.data);
      if (error.response?.data?.message) {
        const errorMsg = Array.isArray(error.response.data.message) 
          ? error.response.data.message.join(', ')
          : error.response.data.message;
        setErrors({ submit: errorMsg });
        alert(`Erro: ${errorMsg}`);
      } else {
        setErrors({ submit: 'Erro ao salvar imóvel. Verifique o console.' });
        alert('Erro ao salvar imóvel. Verifique o console do navegador para mais detalhes.');
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedProperty) return;

    try {
      await api.delete(`/properties/${selectedProperty.id}`);
      await loadProperties();
      setShowDeleteModal(false);
      setSelectedProperty(null);
    } catch (error) {
      console.error('Erro ao excluir imóvel:', error);
    }
  };

  const filteredProperties = properties.filter((p) => {
    const matchesSearch =
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.address?.street?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.address?.neighborhood?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'ALL' || p.type === filterType;
    const matchesStatus = filterStatus === 'ALL' || p.status === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const statusColors: Record<string, string> = {
    AVAILABLE: 'bg-green-100 text-green-800',
    RENTED: 'bg-blue-100 text-blue-800',
    SOLD: 'bg-gray-100 text-gray-800',
    MAINTENANCE: 'bg-yellow-100 text-yellow-800',
  };

  const statusLabels: Record<string, string> = {
    AVAILABLE: 'Disponível',
    RENTED: 'Alugado',
    SOLD: 'Vendido',
    MAINTENANCE: 'Manutenção',
  };

  const typeLabels: Record<string, string> = {
    APARTMENT: 'Apartamento',
    HOUSE: 'Casa',
    COMMERCIAL: 'Comercial',
    LAND: 'Terreno',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Imóveis</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleImportSite}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:text-dark-text-primary dark:border-dark-border"
            title="Importar imóveis do site público (com fotos)"
          >
            {importing ? <Loader className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            <span>{importing ? 'Importando...' : 'Importar do site'}</span>
          </button>
          <button onClick={() => handleOpenModal()} className="btn-primary flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Novo Imóvel</span>
          </button>
        </div>
      </div>

      {importMsg && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-lg px-4 py-3">
          {importMsg}
        </div>
      )}

      <div className="card space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por código, título ou endereço..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>

        <div className="flex space-x-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input"
          >
            <option value="ALL">Todos os Tipos</option>
            <option value="HOUSE">Casa</option>
            <option value="APARTMENT">Apartamento</option>
            <option value="COMMERCIAL">Comercial</option>
            <option value="LAND">Terreno</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input"
          >
            <option value="ALL">Todos os Status</option>
            <option value="AVAILABLE">Disponível</option>
            <option value="RENTED">Alugado</option>
            <option value="SOLD">Vendido</option>
            <option value="MAINTENANCE">Manutenção</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProperties.map((property) => {
          const firstPhoto = property.media && property.media.length > 0 ? property.media[0].url : null;
          const rentPrice = toNumber(property.rentPrice) || 0;
          const salePrice = toNumber(property.salePrice) || 0;
          const seguroFianca = toNumber(property.seguroFianca) || 0;
          const seguroIncendio = toNumber(property.seguroIncendio) || 0;
          const ownerNames =
            property.owners?.map((owner) => owner.person?.name).filter(Boolean).join(', ') || '';
          
          return (
            <div key={property.id} className="card hover:shadow-lg transition-shadow">
              <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                {firstPhoto ? (
                  <img src={firstPhoto} alt={property.title} className="w-full h-full object-cover" />
                ) : (
                  <HomeIcon className="w-16 h-16 text-gray-400" />
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">{property.code}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[property.status]}`}>
                    {statusLabels[property.status]}
                  </span>
                </div>

                <h3 className="font-bold text-lg line-clamp-1">{property.title}</h3>

                <p className="text-sm text-gray-600">{typeLabels[property.type]}</p>

                {property.address && (
                  <div className="flex items-start text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">
                      {property.address.street}
                      {property.address.number && `, ${property.address.number}`}
                      {property.address.neighborhood && ` - ${property.address.neighborhood}`}
                    </span>
                  </div>
                )}

                {(property.bedrooms || property.bathrooms) && (
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    {property.bedrooms && property.bedrooms > 0 && (
                      <div className="flex items-center">
                        <Bed className="w-4 h-4 mr-1" />
                        <span>{property.bedrooms}</span>
                      </div>
                    )}
                    {property.bathrooms && property.bathrooms > 0 && (
                      <div className="flex items-center">
                        <Bath className="w-4 h-4 mr-1" />
                        <span>{property.bathrooms}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-3 border-t space-y-1">
                  {rentPrice > 0 && (
                    <p className="text-lg font-bold text-primary-600">
                      R$ {rentPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês
                    </p>
                  )}
                  {salePrice > 0 && (
                    <p className="text-lg font-bold text-green-600">
                      R$ {salePrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  {seguroFianca > 0 && (
                    <p className="text-sm text-gray-700">
                      Seguro Fiança: R$ {seguroFianca.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  {seguroIncendio > 0 && (
                    <p className="text-sm text-gray-700">
                      Seguro Incêndio: R$ {seguroIncendio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  {ownerNames && <p className="text-sm text-gray-700">Proprietário: {ownerNames}</p>}
                </div>

                <div className="flex space-x-2 pt-2">
                  <button
                    onClick={() => handleOpenModal(property)}
                    className="flex-1 px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors flex items-center justify-center space-x-1"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedProperty(property);
                      setShowDeleteModal(true);
                    }}
                    className="flex-1 px-3 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center space-x-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Excluir</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredProperties.length === 0 && (
        <div className="text-center py-12">
          <HomeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum imóvel encontrado</p>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={selectedProperty ? 'Editar Imóvel' : 'Novo Imóvel'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Fotos */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Fotos do Imóvel</h3>
              <span className="text-sm text-gray-500">{photos.length}/20 fotos</span>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-4">
              {photos.map((photo, index) => (
                <div key={index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
                  <img src={photo.url} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {photos.length < 20 && (
                <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-2">Adicionar</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </label>
              )}
            </div>

            <p className="text-xs text-gray-500">
              Você pode adicionar até 20 fotos. Formatos aceitos: JPG, PNG, WEBP
            </p>
          </div>

          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Código"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              error={errors.code}
              required
            />

            <Select
              label="Tipo"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              options={[
                { value: 'HOUSE', label: 'Casa' },
                { value: 'APARTMENT', label: 'Apartamento' },
                { value: 'COMMERCIAL', label: 'Comercial' },
                { value: 'LAND', label: 'Terreno' },
              ]}
              required
            />

            <Select
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              options={[
                { value: 'AVAILABLE', label: 'Disponível' },
                { value: 'RENTED', label: 'Alugado' },
                { value: 'SOLD', label: 'Vendido' },
                { value: 'MAINTENANCE', label: 'Manutenção' },
              ]}
              required
            />
          </div>

          <Input
            label="Título"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            error={errors.title}
            placeholder="Ex: Apartamento 2 Quartos Centro"
            required
          />

          <Textarea
            label="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descrição detalhada do imóvel..."
            rows={3}
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Input
              label="Quartos"
              type="number"
              value={formData.bedrooms}
              onChange={(e) => setFormData({ ...formData, bedrooms: Number(e.target.value) })}
              min="0"
            />

            <Input
              label="Banheiros"
              type="number"
              value={formData.bathrooms}
              onChange={(e) => setFormData({ ...formData, bathrooms: Number(e.target.value) })}
              min="0"
            />

            <Input
              label="Vagas"
              type="number"
              value={formData.parkingSpaces}
              onChange={(e) => setFormData({ ...formData, parkingSpaces: Number(e.target.value) })}
              min="0"
            />

            <Input
              label="Área Total (m²)"
              type="number"
              value={formData.totalArea}
              onChange={(e) => setFormData({ ...formData, totalArea: Number(e.target.value) })}
              min="0"
              step="0.01"
            />

            <Input
              label="Área Útil (m²)"
              type="number"
              value={formData.usableArea}
              onChange={(e) => setFormData({ ...formData, usableArea: Number(e.target.value) })}
              min="0"
              step="0.01"
            />

            <Input
              label="Área Construída (m²)"
              type="number"
              value={formData.builtArea}
              onChange={(e) => setFormData({ ...formData, builtArea: Number(e.target.value) })}
              min="0"
              step="0.01"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Valor de Venda (R$)"
              type="text"
              inputMode="decimal"
              value={formatCurrencyBRL(formData.salePrice)}
              onChange={(e) => setFormData({ ...formData, salePrice: parseCurrencyBRL(e.target.value) })}
            />

            <Input
              label="Valor de Locação (R$)"
              type="text"
              inputMode="decimal"
              value={formatCurrencyBRL(formData.rentPrice)}
              onChange={(e) => setFormData({ ...formData, rentPrice: parseCurrencyBRL(e.target.value) })}
            />

            <Input
              label="IPTU (R$)"
              type="text"
              inputMode="decimal"
              value={formatCurrencyBRL(formData.iptuValue)}
              onChange={(e) => setFormData({ ...formData, iptuValue: parseCurrencyBRL(e.target.value) })}
            />

            <Input
              label="Condomínio (R$)"
              type="text"
              inputMode="decimal"
              value={formatCurrencyBRL(formData.condoFee)}
              onChange={(e) => setFormData({ ...formData, condoFee: parseCurrencyBRL(e.target.value) })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Seguro Fianca (R$)"
              type="text"
              inputMode="decimal"
              value={formatCurrencyBRL(formData.seguroFianca)}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  seguroFianca: parseCurrencyBRL(e.target.value),
                })
              }
            />

            <Input
              label="Seguro Incendio (R$)"
              type="text"
              inputMode="decimal"
              value={formatCurrencyBRL(formData.seguroIncendio)}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  seguroIncendio: parseCurrencyBRL(e.target.value),
                })
              }
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Proprietário"
              value={formData.ownerPersonId}
              onChange={(e) => setFormData({ ...formData, ownerPersonId: e.target.value })}
              options={people.map((person) => ({
                value: person.id,
                label: person.document ? `${person.name} (${person.document})` : person.name,
              }))}
            />

            <Input
              label="Percentual do Proprietário (%)"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={formData.ownerPercentage}
              onChange={(e) => setFormData({ ...formData, ownerPercentage: Number(e.target.value) })}
              disabled={!formData.ownerPersonId}
            />
          </div>
          {/* Endereço com Busca CEP */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Endereço</h3>
              {toNumber(formData.latitude) !== undefined && toNumber(formData.longitude) !== undefined && (
                <a
                  href={getGoogleMapsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                >
                  <MapPin className="w-4 h-4" />
                  <span>Ver no Google Maps</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="relative">
                  <Input
                    label="CEP"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    onBlur={handleCepBlur}
                    placeholder="80000-000"
                    maxLength={9}
                  />
                  {loadingCep && (
                    <Loader className="absolute right-3 top-9 w-4 h-4 animate-spin text-primary-600" />
                  )}
                </div>

                <div className="col-span-2">
                  <Input
                    label="Logradouro"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    error={errors.address}
                    placeholder="Rua, Avenida, etc."
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Número"
                  value={formData.addressNumber}
                  onChange={(e) => setFormData({ ...formData, addressNumber: e.target.value })}
                  placeholder="123"
                />

                <div className="col-span-2">
                  <Input
                    label="Complemento"
                    value={formData.addressComplement}
                    onChange={(e) => setFormData({ ...formData, addressComplement: e.target.value })}
                    placeholder="Apto 101, Bloco A, etc."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Bairro"
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  placeholder="Centro"
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Cidade"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />

                  <Input
                    label="UF"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-blue-800 font-medium text-sm flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Coordenadas GPS
                  </p>
                  <button
                    type="button"
                    onClick={() => fetchCoordinates(formData.address, formData.city, formData.state)}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Buscar pelo endereço
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Latitude"
                    type="number"
                    step="any"
                    value={formData.latitude || ''}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value as any })}
                    placeholder="-25.397467"
                  />
                  <Input
                    label="Longitude"
                    type="number"
                    step="any"
                    value={formData.longitude || ''}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value as any })}
                    placeholder="-49.189321"
                  />
                </div>
                <p className="text-xs text-blue-600 mt-1 mb-2">
                  Preenchidas automaticamente pelo endereço — você pode colar do Google Maps
                  (latitude, longitude), digitar, ou usar o mapa abaixo.
                </p>
                <LocationPicker
                  lat={toNumber(formData.latitude)}
                  lng={toNumber(formData.longitude)}
                  onChange={(la, ln) =>
                    setFormData({ ...formData, latitude: la as any, longitude: ln as any })
                  }
                />
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errors.submit}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleCloseModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              {selectedProperty ? 'Salvar Alterações' : 'Criar Imóvel'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmar Exclusão"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Tem certeza que deseja excluir o imóvel <strong>{selectedProperty?.title}</strong>?
          </p>
          <p className="text-sm text-red-600">Esta ação não pode ser desfeita.</p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Excluir
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

